/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { pluginsCreatePlugin } from "../funcs/pluginsCreatePlugin.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Plugin } from "../models/components/plugin.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreatePluginRequest,
  CreatePluginSecurity,
} from "../models/operations/createplugin.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreatePluginMutationVariables = {
  request: CreatePluginRequest;
  security?: CreatePluginSecurity | undefined;
  options?: RequestOptions;
};

export type CreatePluginMutationData = Plugin;

export type CreatePluginMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createPlugin plugins
 *
 * @remarks
 * Create a new plugin.
 */
export function useCreatePluginMutation(
  options?: MutationHookOptions<
    CreatePluginMutationData,
    CreatePluginMutationError,
    CreatePluginMutationVariables
  >,
): UseMutationResult<
  CreatePluginMutationData,
  CreatePluginMutationError,
  CreatePluginMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreatePluginMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreatePlugin(): MutationKey {
  return ["@gram/client", "plugins", "createPlugin"];
}

export function buildCreatePluginMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreatePluginMutationVariables,
  ) => Promise<CreatePluginMutationData>;
} {
  return {
    mutationKey: mutationKeyCreatePlugin(),
    mutationFn: function createPluginMutationFn({
      request,
      security,
      options,
    }): Promise<CreatePluginMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(pluginsCreatePlugin(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
